import React, {useMemo} from 'react'
import {Errors, Field, TextInput, validation, WithForm} from '@startlibs/form'
import {postFetcher} from '@startlibs/utils'
import {confirmPasswords, getErrorForField} from '../lib/validation'
import {SplitColumnsContainer} from '@startlibs/components'
import {
  AdditionalInfo,
  Card,
  CardActionButton,
  CardHeader,
  FieldRequirements,
  SignInLayout
} from '../components/SigninLayout'
import {loginFailure} from './LoginForm'
import {PasswordInput} from '../components/PasswordInput'
import {ActivationSuccess} from './ActivationSuccess'
import {FormattedMessage, useIntl} from "react-intl";
import {allRequired} from "./JwtRegistrationForm";
import {useToggle} from "@startlibs/core";

const TextInputMaybeLocked = ({ value, form, path, ...rest }) =>
  value
    ? <TextInput
      {...rest}
      raw
      value={value}
      disabled
      locked
    />
    : <TextInput
      {...rest}
      tabIndex={1}
      path={path}
    />


export const RegistrationForm = ({
                                   userEmail,
                                   userFirstName,
                                   userLastName,
                                   newRegistration,
                                   idToken,
                                   registrationValues,
                                   system,
                                   newRegistrationForUsername
                                 }) => {
  const success = useToggle()
  const visiblePassword = useToggle()
  const activated = useToggle()
  const intl = useIntl()
  const onFailure = validation.responseFailure(
    (errorResponse) => {
      const { type, message, fieldErrors } = errorResponse
      const passwordError = loginFailure(intl)(errorResponse, intl.formatMessage({
        defaultMessage: "The current password is incorrect.",
        description: "Current password error message"
      }))
      return ((message === 'error.validation' || message === 'error.http.401') && Array.isArray(fieldErrors) && fieldErrors.reduce((acc, { field }) => ({ ...acc, ...getErrorForField(field) }), {})) ||
      (type === 'https://pas.purview.net/problem/invalid-password' && { password: true }) ||
      (message === 'error.emailexists' && { email: [intl.formatMessage({
          defaultMessage: 'This email is already registered',
          description: 'Registration error message for already registered email'
        })] }) ||
        passwordError
      }
  )

  const onSuccess = (values, { id_token }) => {
    if (id_token) {
      activated.openWith(id_token)
    } else {
      localStorage.setItem('hasLogged', 'true')
      success.openWith(values)
    }
  }

  const handleSubmit = (values) => {
    const submit = (bearer = idToken) => postFetcher('/pasapi/register',values,{headers:{ 'Content-Type': 'application/json','Authorization': `Bearer ${bearer}`}})
    if (newRegistrationForUsername) {
      return postFetcher(`/pasapi/authenticate?locale=${intl.locale}`, {
        username: newRegistrationForUsername,
        password: values.currentPassword,
        systemId: system.systemId,
        rememberMe: false
      })
        .then(({ idToken }) => {
          return submit(idToken)
        })
    } else {
      return submit()
    }
  }


  const values = useMemo(() => ({
    rememberMe: false,
    systemId: system.systemId,
    ...registrationValues,
    ...(userEmail ? { email: userEmail } : {}),
    ...(userFirstName ? { firstName: userFirstName } : {}),
    ...(userLastName ? { lastName: userLastName } : {})
  }),[])

  const { username, email} = values
  const signupFromOtherMeans = username && !!email
  const preValidation = validation.buildValidation({
    password: [allRequired(intl), confirmPasswords(intl, 'newPassword')],
    firstName: [allRequired(intl)],
    lastName: [allRequired(intl)],
    newPassword: [allRequired(intl), confirmPasswords(intl, 'password')],
    email: [allRequired(intl)],
    ...(newRegistrationForUsername ? { currentPassword: [allRequired(intl)] } : {})
  })
  if (activated.isOpen) {
    return <ActivationSuccess token={activated.isOpen} system={system} newRegistration />
  }
  return <SignInLayout system={system}>
    {!success.isOpen ?
      <Card>
        <CardHeader>
          <h1>{signupFromOtherMeans
            ? intl.formatMessage({defaultMessage:"Registration",description:"Registration title, signup from other means"})
            : newRegistration
              ? intl.formatMessage({defaultMessage:'User registration',description:'User registration title'})
              : intl.formatMessage({defaultMessage:'User registration update',description:'User registration update title'})
          }</h1>
          {
            newRegistration
              ? <p><FormattedMessage
                defaultMessage="Please fill in the fields below to register your account."
                description="Registration instructions"
              /></p>
              : <p><FormattedMessage
                defaultMessage="Please fill in the fields below to enable <b>enhanced authentication security</b> for your account."
                description="Registration update instructions"
                values={{
                  b: chunks => <b>{chunks}</b>
                }}
              /></p>
          }
        </CardHeader>
        <WithForm
          alwaysSave
          preValidation={preValidation}
          onSuccess={onSuccess}
          onFailure={onFailure}
          values={values}
          action={handleSubmit}
        >{form => <>
          <SplitColumnsContainer>
            <TextInput
              raw
              label={intl.formatMessage({
                defaultMessage: "Username",
                description: "Registration username field label"
              })}
              value={username || newRegistrationForUsername}
              locked
              disabled
            />
            <TextInputMaybeLocked
              value={email || userEmail}
              tabIndex={1}
              label={intl.formatMessage({
                defaultMessage: "Email",
                description: "Registration email field label"
              })}
              path="email"
            />
          </SplitColumnsContainer>
          <SplitColumnsContainer>
            <TextInput
              tabIndex={1}
              label={intl.formatMessage({
                defaultMessage: "First name",
                description: "Registration first name field label"
              })}
              path="firstName"
            />
            <TextInput
              tabIndex={1}
              label={intl.formatMessage({
                defaultMessage: "Last name",
                description: "Registration last name field label"
              })}
              path="lastName"
            />
          </SplitColumnsContainer>
          {
            newRegistrationForUsername &&
            <PasswordInput
              tabIndex={1}
              path="currentPassword"
              label={intl.formatMessage({
                defaultMessage: "Current password",
                description: "Registration current password field label"
              })}
            />
          }
          <SplitColumnsContainer>
            <Field label={signupFromOtherMeans ?
              intl.formatMessage({
                defaultMessage: "Create password",
                description: "Registration password field label"
              }): intl.formatMessage({
                defaultMessage:"New password",
                description:"Registration new password field label"
              })}>
              <PasswordInput
                tabIndex={1}
                path="password"
                syncVisible={visiblePassword}
                withoutField
              />
            </Field>
            <Field label={intl.formatMessage({
              defaultMessage: "Confirm new password",
              description: "Registration confirm password field label"
            })}>
              <PasswordInput
                tabIndex={1}
                onPaste={(e) => e.preventDefault()}
                path="newPassword"
                syncVisible={visiblePassword}
                withoutField
              />
            </Field>
          </SplitColumnsContainer>
          <FieldRequirements hasErrors={form.hasErrors('password')}>
            <FormattedMessage
              defaultMessage="Your new password must be <b>{pwdMinimumLength} or more characters</b>, containing at least <b>one uppercase letter</b>, one <b>number</b> and one <b>special character</b>"
              description="Password requirements"
              values={{
                pwdMinimumLength: system.systemAuthPolicy.pwdMinimumLength,
                b: msg => <b>{msg}</b>
              }}
            />
          </FieldRequirements>
          <Errors />
          <CardActionButton
            isLoading={form.isLoading}
            className="highlight with-loader"
            tabIndex={1}
            type="submit"
            highlight
          >
            {newRegistration ? intl.formatMessage({
              defaultMessage: "Register",
              description: "Registration submit button"
            }): intl.formatMessage({
              defaultMessage:"Save",
              description: "Registration update submit button"
            })}
          </CardActionButton>
        </>}</WithForm>
      </Card> :
      <Card>
        <CardHeader>
          <h1><FormattedMessage
            defaultMessage="Almost there! Check your email"
            description="Registration success title"
          /></h1>
        </CardHeader>
        <FormattedMessage
          defaultMessage={`<p>We have e-mailed you a link to activate your account.</p> 
              <p>Please check <b>{email}</b> and click the activation link to finish your registration update.</p>
              <info>If you do not find the email in your inbox, please check your spam or junk folder.</info>`}
          description="Registration success message"
          values={{
            email: success.isOpen.email,
            p: chunks => <p>{chunks}</p>,
            b: chunks => <b>{chunks}</b>,
            info: chunks => <AdditionalInfo>{chunks}</AdditionalInfo>,
          }}
        />
      </Card>}
  </SignInLayout>

}